import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, combineLatest, filter, map, startWith, switchMap, tap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TestCaseLinksService } from '../../../services/test-case-links.service';
import {
  AddedLink,
  CreateTestCaseLinkRequest,
  FailedLinkDraft,
  LinkSaveFailure,
  SavedLinkDraft,
  TestCaseLink,
} from '../../../models/test-case-link.model';
import { LINK_TYPE_META, LinkTypeMeta } from '../../../shared/link-type-meta';
import { IconComponent } from '../../../components/icon/icon.component';
import { RequirementTypeLabelPipe } from '../../../shared/requirement-type-label.pipe';
import { environment } from '../../../../environments/environment';

type RowError = Exclude<LinkSaveFailure, 'other'>;

@Component({
  selector: 'app-test-case-links',
  imports: [TranslatePipe, MatIcon, RouterLink, IconComponent, RequirementTypeLabelPipe],
  templateUrl: './test-case-links.component.html',
  styleUrl: './test-case-links.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseLinksComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly linksService = inject(TestCaseLinksService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  readonly readOnly = input(false);

  readonly links = signal<TestCaseLink[]>([]);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly mutationError = signal(false);
  readonly placeholderRows = [0, 1, 2];

  /**
   * Interne Draft-Repräsentation: koppelt jeden Draft an seine synthetische
   * ID (`draft-N`), damit `onRemove` per ID matcht statt per Inhalt. Ohne
   * diese Kopplung entfernte ein Klick bei Duplikat-Inhalten (gleicher Typ
   * + gleiche Referenz) beide Drafts aus dem Pending-State — die sichtbare
   * Zeile blieb im `links()`-Signal, wurde beim Save aber nicht angelegt
   * (WYSIWYG-Bruch, NEX-714-Review).
   */
  private readonly draftEntries = signal<{ draftId: string; request: CreateTestCaseLinkRequest }[]>([]);
  readonly draftLinks = computed(() => this.draftEntries().map((e) => e.request));
  readonly pendingDeletions = signal<string[]>([]);
  private readonly rowErrors = signal<ReadonlyMap<string, RowError>>(new Map());
  readonly draftLinksChange = output<CreateTestCaseLinkRequest[]>();
  readonly pendingDeletionsChange = output<string[]>();
  readonly openModalRequested = output<void>();

  readonly requirementKeys = computed(() => [
    ...new Set(
      this.links()
        .filter((l) => l.type === 'REQUIREMENT')
        .map((l) => l.reference.trim()),
    ),
  ]);
  readonly hasPendingChanges = computed(() => this.draftLinks().length > 0 || this.pendingDeletions().length > 0);
  private draftIdSeq = 0;
  private readonly removedLinks = new Map<string, TestCaseLink>();
  private readonly reload$ = new Subject<void>();

  constructor() {
    combineLatest([toObservable(this.testCaseId), this.reload$.pipe(startWith(undefined))])
      .pipe(
        map(([id]) => id),
        filter((id): id is string => !!id),
        tap(() => {
          this.loading.set(true);
          this.loadError.set(false);
        }),
        switchMap((id) =>
          this.linksService.list(this.projectId(), id).pipe(
            catchError(() => {
              this.loadError.set(true);
              this.loading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((links) => {
        this.links.set(links);
        this.loading.set(false);
      });
  }

  reload(): void {
    this.reload$.next();
  }

  metaFor(link: TestCaseLink): LinkTypeMeta {
    return LINK_TYPE_META[link.type];
  }

  rowError(link: TestCaseLink): RowError | null {
    return this.rowErrors().get(link.id) ?? null;
  }

  /** Bei Anforderungen ersetzt der Titel das Anzeige-Label, wie im Design. */
  primaryText(link: TestCaseLink): string {
    if (link.type === 'REQUIREMENT') {
      return link.requirementTitle ? `${link.reference} · ${link.requirementTitle}` : link.reference;
    }
    return link.displayLabel || link.reference;
  }

  sourceKey(link: TestCaseLink): string {
    return link.requirementSourceType
      ? `testCases.form.links.source.${link.requirementSourceType}`
      : 'testCases.form.links.types.REQUIREMENT';
  }

  /** Externe Ziel-URL oder null, wenn der Typ keinen extern oeffenbaren Link darstellt. */
  externalUrl(link: TestCaseLink): string | null {
    switch (link.type) {
      case 'URL':
        return link.reference;
      case 'JIRA':
        return `${environment.jiraBrowseBase}${link.reference}`;
      case 'GITHUB':
        return this.gitHubUrl(link.reference);
      default:
        return null;
    }
  }

  /** Null keeps unresolved references and drafts without a picked requirement as plain text. */
  requirementRoute(link: TestCaseLink): string[] | null {
    if (link.type !== 'REQUIREMENT' || !link.requirementId) {
      return null;
    }
    return ['/projects', this.projectId(), 'requirements', link.requirementId, 'edit'];
  }

  /**
   * `owner/repo#42` wird zum Issue-/PR-Link aufgeloest (GitHub leitet `/issues/<n>` bei PRs
   * automatisch nach `/pull/<n>` um); reine `owner/repo`-Angaben bleiben Repo-Links.
   */
  private gitHubUrl(reference: string): string {
    if (reference.startsWith('http')) {
      return reference;
    }
    const issue = /^([^/]+\/[^/#]+)#(\d+)$/.exec(reference);
    if (issue) {
      return `https://github.com/${issue[1]}/issues/${issue[2]}`;
    }
    return `https://github.com/${reference}`;
  }

  requestAddModal(): void {
    if (this.readOnly()) {
      return;
    }
    this.openModalRequested.emit();
  }

  onAdd({ request, requirement }: AddedLink): void {
    if (this.restoreRemovedRequirement(request)) {
      return;
    }
    const draftId = `draft-${++this.draftIdSeq}`;
    const draft: TestCaseLink = {
      id: draftId,
      testCaseId: this.testCaseId() ?? '',
      type: request.type,
      reference: request.reference,
      displayLabel: request.displayLabel ?? null,
      createdAt: new Date().toISOString(),
      requirementId: requirement?.id ?? null,
      requirementTitle: requirement?.title ?? null,
      requirementType: requirement?.type ?? null,
      requirementStatus: requirement?.status ?? null,
      requirementSourceType: requirement?.sourceType ?? null,
    };
    this.links.update((current) => [...current, draft]);
    this.draftEntries.update((current) => [...current, { draftId, request }]);
    this.draftLinksChange.emit(this.draftLinks());
  }

  onRemove(link: TestCaseLink): void {
    if (this.readOnly()) {
      return;
    }
    this.links.update((current) => current.filter((l) => l.id !== link.id));
    if (link.id.startsWith('draft-')) {
      this.draftEntries.update((current) => current.filter((e) => e.draftId !== link.id));
      this.rowErrors.update((errors) => new Map([...errors].filter(([id]) => id !== link.id)));
      this.draftLinksChange.emit(this.draftLinks());
      return;
    }
    this.removedLinks.set(link.id, link);
    const next = [...this.pendingDeletions(), link.id];
    this.pendingDeletions.set(next);
    this.pendingDeletionsChange.emit(next);
  }

  /**
   * Re-adding a requirement whose saved link is marked for deletion cancels that deletion.
   * A new draft would be created before the old link is deleted and fail as duplicate (409).
   */
  private restoreRemovedRequirement(request: CreateTestCaseLinkRequest): boolean {
    if (request.type !== 'REQUIREMENT') {
      return false;
    }
    const reference = request.reference.trim();
    const removed = this.pendingDeletions()
      .map((id) => this.removedLinks.get(id))
      .find((l) => l?.type === 'REQUIREMENT' && l.reference.trim() === reference);
    if (!removed) {
      return false;
    }
    this.links.update((current) => [...current, removed]);
    const next = this.pendingDeletions().filter((id) => id !== removed.id);
    this.pendingDeletions.set(next);
    this.pendingDeletionsChange.emit(next);
    return true;
  }

  resetPending(): void {
    this.draftEntries.set([]);
    this.pendingDeletions.set([]);
    this.rowErrors.set(new Map());
    this.mutationError.set(false);
  }

  /**
   * Nach einem Save mit Fehlern reconciled der Parent den Kind-State: erfolgreich
   * angelegte Drafts (Request-Referenz-Identisch aus dem Emit-Zyklus) werden durch
   * den gespeicherten Link ersetzt, erledigte Deletions entfernt. Ohne diese Reconciliation blieben die
   * bereits persistierten Drafts als `draft-N`-Zeilen sichtbar; der naechste
   * `draftLinksChange`-Emit ueberschriebe das Form-`pendingLinkDrafts` wieder
   * mit allen Drafts (inkl. der erfolgreichen) — der naechste Save legt sie
   * dann doppelt an (NEX-714 Review Runde 2, MZemelkaST).
   * 404 und 409 kennzeichnen die Zeile, alle anderen Fehler die ganze Karte.
   */
  applySaveResult(
    successfulDrafts: SavedLinkDraft[],
    successfulDeletionIds: string[],
    failedDrafts: FailedLinkDraft[],
    deletionFailed: boolean,
  ): void {
    const entries = this.draftEntries();
    const draftIdOf = new Map(entries.map((e) => [e.request, e.draftId]));
    const errors = new Map<string, RowError>();
    for (const { request, reason } of failedDrafts) {
      const draftId = draftIdOf.get(request);
      if (draftId && reason !== 'other') {
        errors.set(draftId, reason);
      }
    }
    this.rowErrors.set(errors);
    this.mutationError.set(deletionFailed || failedDrafts.some((f) => f.reason === 'other'));

    if (successfulDrafts.length > 0) {
      const savedByDraftId = new Map<string, TestCaseLink>();
      for (const { request, link } of successfulDrafts) {
        const draftId = draftIdOf.get(request);
        if (draftId) {
          savedByDraftId.set(draftId, link);
        }
      }
      this.draftEntries.set(entries.filter((e) => !savedByDraftId.has(e.draftId)));
      this.links.update((current) => current.map((l) => savedByDraftId.get(l.id) ?? l));
      this.draftLinksChange.emit(this.draftLinks());
    }
    if (successfulDeletionIds.length > 0) {
      const doneSet = new Set(successfulDeletionIds);
      const next = this.pendingDeletions().filter((id) => !doneSet.has(id));
      this.pendingDeletions.set(next);
      this.pendingDeletionsChange.emit(next);
    }
  }
}
